import { useCallback, useEffect, useState } from "react";

const getWrappedIndex = (currentIndex, itemCount, direction) => {
  if (itemCount <= 0) return 0;
  return (currentIndex + direction + itemCount) % itemCount;
};

export const useDropdownNavigation = ({
  itemCount,
  listRef,
  optionIndexAttribute,
  isActive = true,
  stickyTopSelector,
}) => {
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  const resetHighlightIndex = useCallback(() => {
    setHighlightedIndex(0);
  }, []);

  useEffect(() => {
    if (itemCount <= 0) {
      setHighlightedIndex(0);
      return;
    }

    setHighlightedIndex((previousIndex) =>
      Math.min(previousIndex, itemCount - 1)
    );
  }, [itemCount]);

  useEffect(() => {
    if (!isActive || itemCount <= 0) return;

    const listElement = listRef.current;
    if (!(listElement instanceof HTMLElement)) return;

    const selectedElement = listElement.querySelector(
      `[${optionIndexAttribute}="${highlightedIndex}"]`
    );
    if (!(selectedElement instanceof HTMLElement)) return;

    const stickyTopHeight = stickyTopSelector
      ? listElement.querySelector(stickyTopSelector)?.getBoundingClientRect()
        .height || 0
      : 0;

    if (!stickyTopHeight) {
      selectedElement.scrollIntoView({ block: "nearest" });
      return;
    }

    const optionTop = selectedElement.offsetTop;
    const optionBottom = optionTop + selectedElement.offsetHeight;
    const visibleTop = listElement.scrollTop + stickyTopHeight;
    const visibleBottom = listElement.scrollTop + listElement.clientHeight;

    if (optionTop < visibleTop) {
      listElement.scrollTop = Math.max(0, optionTop - stickyTopHeight - 4);
      return;
    }

    if (optionBottom > visibleBottom) {
      listElement.scrollTop = optionBottom - listElement.clientHeight + 4;
    }
  }, [
    highlightedIndex,
    isActive,
    itemCount,
    listRef,
    optionIndexAttribute,
    stickyTopSelector,
  ]);

  const moveHighlightDown = useCallback(() => {
    if (itemCount <= 0) return;
    setHighlightedIndex((previousIndex) =>
      getWrappedIndex(previousIndex, itemCount, 1)
    );
  }, [itemCount]);

  const moveHighlightUp = useCallback(() => {
    if (itemCount <= 0) return;
    setHighlightedIndex((previousIndex) =>
      getWrappedIndex(previousIndex, itemCount, -1)
    );
  }, [itemCount]);

  const handleNavigationKeyDown = useCallback(
    (event, { onSelectIndex, onEscape } = {}) => {
      if (event.key === "Escape" && onEscape) {
        event.preventDefault();
        onEscape();
        return true;
      }

      if (itemCount <= 0) {
        return false;
      }

      if (event.key === "ArrowDown") {
        event.preventDefault();
        moveHighlightDown();
        return true;
      }

      if (event.key === "ArrowUp") {
        event.preventDefault();
        moveHighlightUp();
        return true;
      }

      if ((event.key === "Enter" || event.key === "Tab") && onSelectIndex) {
        event.preventDefault();
        onSelectIndex(highlightedIndex);
        return true;
      }

      return false;
    },
    [highlightedIndex, itemCount, moveHighlightDown, moveHighlightUp]
  );

  return {
    highlightedIndex,
    setHighlightedIndex,
    resetHighlightIndex,
    handleNavigationKeyDown,
  };
};
